import React, { useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  CircularProgress,
  Snackbar,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { VpnKey, Add } from '@mui/icons-material';
import { useApiKey } from '../contexts/ApiKeyContext';
import {
  ApiKeyPermissionPicker,
  DEFAULT_STORE_API_KEY_PERMISSIONS,
} from './ApiKeyPermissionPicker';

export const ApiKeyBanner: React.FC = () => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { apiKey, apiKeys, createApiKey, isLoading } = useApiKey();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [keyName, setKeyName] = useState('');
  const [permissions, setPermissions] = useState<string[]>([...DEFAULT_STORE_API_KEY_PERMISSIONS]);
  const [isCreating, setIsCreating] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // Don't show banner if user has an API key
  if (apiKey || apiKeys.length > 0) {
    return null;
  }

  const handleCreateKey = async () => {
    if (!keyName.trim() || permissions.length === 0) return;

    try {
      setIsCreating(true);
      await createApiKey(keyName.trim(), permissions);
      setDialogOpen(false);
      setKeyName('');
      setPermissions([...DEFAULT_STORE_API_KEY_PERMISSIONS]);
      setSnackbarMessage('API key created successfully!');
      setSnackbarOpen(true);
    } catch (error) {
      setSnackbarMessage('Failed to create API key. Please try again.');
      setSnackbarOpen(true);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <>
      <Alert 
        severity="info" 
        sx={{ mb: 2 }}
        action={
          <Button
            color="inherit"
            size="small"
            startIcon={<Add />}
            onClick={() => setDialogOpen(true)}
            disabled={isLoading}
          >
            Generate API Key
          </Button>
        }
      >
        <AlertTitle>No API Key Found</AlertTitle>
        You don't have an API key set up. Generate one to access the API endpoints and sync your camera data with external platforms.
      </Alert>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth fullScreen={fullScreen}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VpnKey />
            <Typography variant="h6">Generate API Key</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Create an API key to access the Product Data API. Choose which endpoint groups this key can call.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Key Name"
            placeholder="e.g., My Integration Key"
            fullWidth
            variant="outlined"
            value={keyName}
            onChange={(e) => setKeyName(e.target.value)}
            disabled={isCreating}
            sx={{ mb: 2 }}
          />
          <ApiKeyPermissionPicker
            value={permissions}
            onChange={setPermissions}
            disabled={isCreating}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={isCreating}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateKey}
            variant="contained"
            disabled={!keyName.trim() || permissions.length === 0 || isCreating}
            startIcon={isCreating ? <CircularProgress size={20} /> : <VpnKey />}
          >
            {isCreating ? 'Creating...' : 'Create Key'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </>
  );
};
